// The numbers on top of the Assistant tabs, on a phone (app-feel plan, builder B).
//
// Swipeable cards, STUCK first (« Bloquées chez nous »), then the Inbox now /
// today, the orders in progress and their MAD. Each card taps to the place it
// counts. The counts are numbers.ts — the same rules as the tabs' own lines,
// so the strip never disagrees with the list under it.
//
// Phone only (`md:hidden`): desktop keeps the per-tab lines it had.
import { useMemo } from 'react';
import { useT } from '@/lib/i18n';
import { usePilotage } from './usePilotage';
import { inboxNumbers, pipelineNumbers, stripCards, type Range, type StripTarget } from './numbers';
import { SwipeStrip } from './SwipeStrip';

const MAD = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n));

const TONE = {
  alert: { card: 'border-error/40 bg-error/[0.07]', value: 'text-error', dot: 'bg-error' },
  warn: { card: 'border-warning/50 bg-warning/10', value: 'text-[#8F5F2A] dark:text-status-waiting', dot: 'bg-warning' },
  calm: { card: 'border-border-subtle bg-bg-card', value: 'text-text-primary', dot: '' },
} as const;

export function NumbersStrip({ range, onGo }: { range: Range; onGo: (to: StripTarget) => void }) {
  const { t } = useT();
  const { data } = usePilotage();
  const cards = useMemo(() => stripCards(
    inboxNumbers(data?.inbox?.items ?? [], range),
    pipelineNumbers(data?.pipeline?.clients ?? [], range),
  ), [data, range]);
  if (!data) return null;

  return (
    <div className="md:hidden">
      <SwipeStrip label={t('Chiffres clés')} className="gap-2 py-0.5">
        {cards.map((c) => {
          const tone = TONE[c.tone];
          return (
            <button
              key={c.key}
              type="button"
              data-strip-key={c.key}
              onClick={() => onGo(c.to)}
              className={`snap-start shrink-0 min-w-[124px] min-h-[64px] rounded-xl border px-3 py-2 text-left cursor-pointer transition-[transform,background-color] duration-150 active:scale-[0.97] motion-reduce:active:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${tone.card}`}
            >
              <span className={`block font-heading text-[22px] leading-none tabular-nums ${tone.value}`}>
                {c.money ? MAD(c.value) : c.value}
                {c.money && <span className="ml-1 font-sans text-[11px] text-text-tertiary">MAD</span>}
              </span>
              <span className="mt-1 flex items-center gap-1.5 text-[12px] text-text-secondary whitespace-nowrap">
                {tone.dot && <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} aria-hidden />}
                {t(c.label)}
              </span>
            </button>
          );
        })}
      </SwipeStrip>
    </div>
  );
}
